import { useMemo, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import MultiStepForm from "@/components/MultiStepForm";
import type { Path } from "@/router";

import CareerGoalsForm from "./_CareerGoalsForm";
import {
  getCareerIntentSteps,
  validateCareerIntentStep,
  type CareerIntentFormValues,
  type CareerIntentStepId
} from "./careerIntentForm";

export default function CareerGoalsWalkthrough({
  currentRole,
  form,
  isSubmitting,
  pgaId,
  onCancel,
  onSubmit
}: {
  currentRole?: string | null;
  form: UseFormReturn<CareerIntentFormValues>;
  isSubmitting: boolean;
  pgaId: string;
  onCancel: () => void;
  onSubmit: (values: CareerIntentFormValues) => Promise<void> | void;
}) {
  const selectedIntents = form.watch("selectedIntents");
  const steps = useMemo(() => getCareerIntentSteps({ selectedIntents }), [selectedIntents]);
  const [stepErrors, setStepErrors] = useState<Partial<Record<CareerIntentStepId, string | undefined>>>({});
  const goalsPath = `/people/${pgaId}/career/career-profile/goals`;

  return (
    <MultiStepForm
      form={form}
      renderStepper
      isEditing
      showActions
      validatePathOnStepClick
      disableSubmitUntilLastStep
      modelName="Check-In"
      submitText="Save Plan"
      submittingText="Saving..."
      isSubmitting={isSubmitting}
      backLink={{ to: goalsPath as Path, title: "Cancel" }}
      onCancel={onCancel}
      onSubmit={onSubmit}
      steps={steps.map((step, index) => ({
        headline: step.headline,
        supportingText: `Step ${index + 1}`,
        validate: async () => {
          const message = validateCareerIntentStep(form.getValues(), step.id);
          setStepErrors((prev) => ({ ...prev, [step.id]: message }));
          return message == null;
        },
        content: (
          <CareerGoalsForm
            currentRole={currentRole}
            disabled={isSubmitting}
            errorMessage={stepErrors[step.id]}
            form={form}
            step={step.id}
            stepTitle={step.title}
          />
        )
      }))}
    />
  );
}
